import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import BlogNavigation from '../blog/components/BlogNavigation';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

interface ATSPlatform {
  name: string;
  status: 'live' | 'soon' | 'capture';
  desc: string;
  tags: string[];
}

const REGION_META: Record<string, { label: string; emoji: string }> = {
  global:    { label: 'Global',              emoji: '🌐' },
  americas:  { label: 'Americas',            emoji: '🇺🇸' },
  ukeu:      { label: 'UK & Europe',         emoji: '🇪🇺' },
  southasia: { label: 'South Asia',          emoji: '🇮🇳' },
  mena:      { label: 'MENA',               emoji: '🇦🇪' },
  apac:      { label: 'Asia-Pacific',        emoji: '🌏' },
  africa:    { label: 'Sub-Saharan Africa',  emoji: '🌍' },
};

const DATA: Record<string, ATSPlatform[]> = {
  global: [
    { name: 'Workday',          status: 'live',    desc: 'The Fortune 500 standard. Used by enterprise companies across every continent.',                                                                                          tags: ['Enterprise', 'HCM Suite'] },
    { name: 'Greenhouse',       status: 'live',    desc: 'Preferred by tech companies globally. Known for structured, bias-free hiring flows.',                                                                                    tags: ['Mid-Market', 'Tech'] },
    { name: 'Lever',            status: 'live',    desc: 'ATS + CRM hybrid under Employ Inc. Common at growth-stage and mid-market companies worldwide.',                                                                          tags: ['Mid-Market', 'Startup'] },
    { name: 'SAP SuccessFactors', status: 'soon',  desc: 'Global MNCs in manufacturing, logistics and banking. SmartRecruiters will replace its recruiting module over 3–5 years.',                                               tags: ['Enterprise', 'HCM Suite'] },
    { name: 'Oracle Taleo',     status: 'soon',    desc: 'Deep roots in banking, healthcare, and government hiring worldwide.',                                                                                                    tags: ['Enterprise', 'Legacy'] },
    { name: 'iCIMS',            status: 'live',    desc: '#1 by market share (10.7%). Dominant in US large-enterprise but deployed globally.',                                                                                     tags: ['Enterprise', 'High-Volume'] },
    { name: 'SmartRecruiters',  status: 'live',    desc: 'Acquired by SAP (Sept 2025). AI-first platform used by Amazon, Visa, McDonald\'s and 4,000+ enterprises globally.',                                                     tags: ['Enterprise', 'AI-Native'] },
    { name: 'Workable',         status: 'live',    desc: 'Easy setup, 160+ country reach. Popular among global SMBs.',                                                                                                            tags: ['SMB', 'Global'] },
    { name: 'Ashby',            status: 'soon',    desc: 'Analytics-heavy platform loved by high-growth startups globally.',                                                                                                      tags: ['Startup', 'Analytics'] },
    { name: 'Rippling',         status: 'soon',    desc: 'HR + IT + Finance in one. Dominant for remote-first global companies.',                                                                                                  tags: ['Mid-Market', 'Remote'] },
    { name: 'BambooHR',         status: 'capture', desc: 'HR suite with ATS used by SMBs across 150+ countries.',                                                                                                                 tags: ['SMB', 'HR Suite'] },
  ],
  americas: [
    { name: 'ADP Recruiting',   status: 'soon',    desc: 'Payroll-integrated ATS. Massive US/Canada penetration across all sizes.',                                                                                               tags: ['Mid-Market', 'Payroll'] },
    { name: 'JazzHR',           status: 'soon',    desc: 'Part of Employ Inc. Purpose-built for US SMBs — typically a company\'s first ATS.',                                                                                     tags: ['SMB', 'US'] },
    { name: 'Paycom',           status: 'capture', desc: 'All-in-one HR for US mid-market. Strong in retail and healthcare.',                                                                                                      tags: ['Mid-Market', 'US'] },
    { name: 'Paychex',          status: 'capture', desc: 'Payroll-first company with embedded ATS for US SMBs.',                                                                                                                  tags: ['SMB', 'US'] },
    { name: 'Jobvite',          status: 'soon',    desc: 'Part of Employ Inc. Focused on US enterprise with advanced analytics and recruitment marketing.',                                                                        tags: ['Enterprise', 'US'] },
    { name: 'ClearCompany',     status: 'capture', desc: 'US mid-market with strong analytics and onboarding features.',                                                                                                          tags: ['Mid-Market', 'US'] },
    { name: 'Bullhorn',         status: 'capture', desc: 'The go-to ATS for staffing agencies across North America.',                                                                                                             tags: ['Staffing', 'Agencies'] },
    { name: 'UKG Pro',          status: 'capture', desc: 'Enterprise US/Canada HR suite with powerful workforce management.',                                                                                                     tags: ['Enterprise', 'US/CA'] },
    { name: 'Ceridian Dayforce', status: 'capture', desc: 'Canada-origin HR cloud. Widely used in enterprise US and Canada.',                                                                                                    tags: ['Enterprise', 'CA/US'] },
    { name: 'Pinpoint',         status: 'soon',    desc: 'Growing fast in US and UK for internal HR teams.',                                                                                                                      tags: ['Mid-Market', 'US/UK'] },
    { name: 'Cornerstone',      status: 'capture', desc: 'Learning + talent platform with ATS. Popular in US enterprise.',                                                                                                        tags: ['Enterprise', 'L&D'] },
    { name: 'Avature',          status: 'capture', desc: 'Highly configurable CRM+ATS used by global enterprise from US base.',                                                                                                   tags: ['Enterprise', 'CRM'] },
  ],
  ukeu: [
    { name: 'Pinpoint',         status: 'soon',    desc: 'UK-born ATS growing rapidly across British and Irish companies.',                                                                                                       tags: ['Mid-Market', 'UK'] },
    { name: 'Tribepad',         status: 'capture', desc: 'UK-focused platform used by public sector and retail.',                                                                                                                 tags: ['Mid-Market', 'UK'] },
    { name: 'Eploy',            status: 'capture', desc: 'UK SMB and mid-market ATS with strong local support.',                                                                                                                  tags: ['SMB', 'UK'] },
    { name: 'Personio',         status: 'soon',    desc: 'Germany-origin HR suite dominating EU mid-market.',                                                                                                                     tags: ['Mid-Market', 'EU'] },
    { name: 'Recruitee',        status: 'soon',    desc: 'Netherlands-born. Popular across Dutch and broader EU SMBs.',                                                                                                           tags: ['SMB', 'EU'] },
    { name: 'Teamtailor',       status: 'soon',    desc: 'Sweden-origin. 12,000+ companies in 90+ countries. Strong in Nordics, UK, and DACH.',                                                                                   tags: ['SMB', 'EU/Nordics'] },
    { name: 'Homerun',          status: 'capture', desc: 'Design-forward Dutch ATS for creative and tech startups.',                                                                                                              tags: ['Startup', 'EU'] },
    { name: 'Softgarden',       status: 'capture', desc: 'German enterprise ATS. Dominant in DACH region.',                                                                                                                      tags: ['Enterprise', 'DACH'] },
    { name: 'Cegid Talentsoft', status: 'capture', desc: 'French-origin. Standard for large French enterprises.',                                                                                                                 tags: ['Enterprise', 'France'] },
    { name: 'Factorial',        status: 'capture', desc: 'Barcelona-origin HR suite. Strong in Spain and Southern EU.',                                                                                                           tags: ['SMB', 'Southern EU'] },
    { name: 'CIPHR',            status: 'capture', desc: 'UK HR suite with ATS, widely used in UK mid-market.',                                                                                                                   tags: ['Mid-Market', 'UK'] },
    { name: 'Hireserve',        status: 'capture', desc: 'UK specialist for public sector and universities.',                                                                                                                     tags: ['Public Sector', 'UK'] },
  ],
  southasia: [
    { name: 'Naukri RMS',       status: 'live',    desc: '#1 in India with the largest resume database. Recruiter standard.',                                                                                                     tags: ['Enterprise', 'India'] },
    { name: 'Rozee.pk',         status: 'live',    desc: 'Pakistan\'s leading job portal and ATS for local and remote roles.',                                                                                                    tags: ['Mid-Market', 'Pakistan'] },
    { name: 'Zoho Recruit',     status: 'soon',    desc: 'Indian-origin, globally used SMB ATS. Affordable and powerful.',                                                                                                        tags: ['SMB', 'Global'] },
    { name: 'Darwinbox',        status: 'soon',    desc: 'Indian unicorn expanding across South and Southeast Asia.',                                                                                                             tags: ['Enterprise', 'India/SEA'] },
    { name: 'Keka',             status: 'capture', desc: 'Fast-growing modern HR suite for Indian mid-market.',                                                                                                                   tags: ['Mid-Market', 'India'] },
    { name: 'PeopleStrong',     status: 'capture', desc: 'Indian enterprise HR platform with deep ATS capabilities.',                                                                                                            tags: ['Enterprise', 'India'] },
    { name: 'Bdjobs',           status: 'capture', desc: 'Bangladesh\'s #1 job platform and employer ATS.',                                                                                                                      tags: ['Mid-Market', 'Bangladesh'] },
    { name: 'Mustakbil',        status: 'capture', desc: 'Pakistan\'s second-largest job portal used by local employers.',                                                                                                       tags: ['SMB', 'Pakistan'] },
  ],
  mena: [
    { name: 'Bayt.com',         status: 'soon',    desc: 'Pan-MENA\'s largest job portal and ATS. UAE, KSA, Egypt core.',                                                                                                        tags: ['Mid-Market', 'Pan-MENA'] },
    { name: 'Wuzzuf',           status: 'soon',    desc: 'Egypt\'s dominant job board and recruitment platform.',                                                                                                                 tags: ['Mid-Market', 'Egypt'] },
    { name: 'Naukrigulf',       status: 'capture', desc: 'GCC-focused extension of Naukri. Popular with Indian expats in Gulf.',                                                                                                  tags: ['Mid-Market', 'GCC'] },
    { name: 'GulfTalent',       status: 'capture', desc: 'Premium platform for professionals across Gulf states.',                                                                                                               tags: ['Mid-Market', 'GCC'] },
    { name: 'Oracle Taleo',     status: 'soon',    desc: 'Saudi Aramco, major banks, and government entities in GCC.',                                                                                                           tags: ['Enterprise', 'GCC Govt'] },
    { name: 'SAP SuccessFactors', status: 'soon',  desc: 'Large Saudi and Emirati conglomerates use SAP as HR backbone.',                                                                                                        tags: ['Enterprise', 'GCC'] },
  ],
  apac: [
    { name: 'PageUp',           status: 'soon',    desc: 'ANZ enterprise standard. Used by Australian universities and large firms.',                                                                                             tags: ['Enterprise', 'ANZ'] },
    { name: 'JobAdder',         status: 'capture', desc: 'ANZ recruitment agency standard. Clean UI and API-first.',                                                                                                             tags: ['Agencies', 'ANZ'] },
    { name: 'Manatal',          status: 'soon',    desc: 'Thailand-origin ATS with strong pan-Southeast Asia penetration.',                                                                                                      tags: ['SMB', 'SEA'] },
    { name: 'Kalibrr',          status: 'capture', desc: 'Philippines-dominant talent platform used by BPO and tech.',                                                                                                           tags: ['Mid-Market', 'Philippines'] },
    { name: 'Jobstreet',        status: 'capture', desc: 'SEEK-owned. Malaysia, Singapore, Philippines job standard.',                                                                                                           tags: ['Mid-Market', 'SEA'] },
    { name: 'Talenox',          status: 'capture', desc: 'Singapore SMB HR and ATS platform. MAS-compliant payroll.',                                                                                                            tags: ['SMB', 'Singapore'] },
    { name: 'Darwinbox',        status: 'soon',    desc: 'Expanding aggressively from India into Indonesia and broader SEA.',                                                                                                    tags: ['Enterprise', 'SEA'] },
  ],
  africa: [
    { name: 'BrighterMonday',   status: 'capture', desc: 'East Africa\'s leading job platform. Kenya, Uganda, Tanzania.',                                                                                                        tags: ['Mid-Market', 'East Africa'] },
    { name: 'Jobberman',        status: 'capture', desc: 'West Africa\'s #1 job marketplace. Nigeria and Ghana dominant.',                                                                                                       tags: ['Mid-Market', 'West Africa'] },
    { name: 'Pnet',             status: 'capture', desc: 'South Africa\'s top recruitment platform for professionals.',                                                                                                          tags: ['Mid-Market', 'South Africa'] },
    { name: 'Careers24',        status: 'capture', desc: 'South Africa\'s second-largest job portal. Strong in retail & finance.',                                                                                               tags: ['Mid-Market', 'South Africa'] },
  ],
};

const totalCount = Object.values(DATA).reduce((acc, curr) => acc + curr.length, 0);
const liveCount  = Object.values(DATA).flat().filter(p => p.status === 'live').length;
const soonCount  = Object.values(DATA).flat().filter(p => p.status === 'soon').length;

export default function ATSPlatformsPage() {
  const [activeTab, setActiveTab]   = useState('all');
  const [search,    setSearch]      = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [collapsedRegions, setCollapsedRegions] = useState<Set<string>>(new Set());

  const toggleRegion = (region: string) => {
    setCollapsedRegions(prev => {
      const next = new Set(prev);
      if (next.has(region)) {
        next.delete(region);
      } else {
        next.add(region);
      }
      return next;
    });
  };

  const collapseAll = () => {
    setCollapsedRegions(new Set(Object.keys(REGION_META)));
  };

  const expandAll = () => {
    setCollapsedRegions(new Set());
  };

  useEffect(() => {
    document.title = 'ATS Platforms Supported by JobOS | Full Compatibility List';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Full list of ATS platforms supported by JobOS. Autofill live on Workday, Greenhouse, iCIMS, Lever and more — across 50+ countries.');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'ATS Platforms Supported by JobOS | Full Compatibility List', true);
    setMeta('og:description', 'Full list of ATS platforms supported by JobOS. Autofill live on Workday, Greenhouse, iCIMS, Lever and more — across 50+ countries.', true);
    setMeta('og:url', `${SITE_URL}/ats-platforms`, true);
    setMeta('og:type', 'website', true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/4f32dbda249789ecc0e9d1bd217207d0.jpg', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'ATS Platforms Supported by JobOS | Full Compatibility List');
    setMeta('twitter:description', 'Full list of ATS platforms supported by JobOS. Autofill live on Workday, Greenhouse, iCIMS, Lever and more — across 50+ countries.');
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Abstract%20digital%20illustration%20of%20a%20global%20applicant%20tracking%20system%20network%20with%20glowing%20indigo%20and%20violet%20nodes%20connected%20by%20luminous%20data%20pathways%20spanning%20a%20dark%20world%20map%20silhouette%2C%20futuristic%20HR%20technology%20concept%20with%20floating%20interface%20panels%20and%20recruitment%20workflow%20diagrams%2C%20deep%20navy%20background%20with%20bright%20indigo%20accent%20lights%20radiating%20outward%2C%20minimal%20clean%20tech%20art%20representing%20worldwide%20ATS%20platform%20coverage%2C%20no%20text%2C%20high%20quality%20digital%20illustration&width=1920&height=840&seq=ats-hero-global-v3&orientation=landscape');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/ats-platforms`);

    // Remove any existing page-level JSON-LD scripts
    document.querySelectorAll('script[data-schema-page="ats-platforms"]').forEach(el => el.remove());

    // Add Schema.org JSON-LD
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'ATS Platforms Supported by JobOS',
        description: 'Full list of ATS platforms supported by JobOS. Autofill live on Workday, Greenhouse, iCIMS, Lever and more — across 50+ countries.',
        url: `${SITE_URL}/ats-platforms`,
        mainEntity: {
          '@type': 'ItemList',
          name: 'Supported ATS Platforms',
          description: 'Complete list of Applicant Tracking Systems supported by JobOS autofill feature',
          numberOfItems: totalCount,
          itemListElement: Object.values(DATA).flat().map((platform, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: platform.name,
            description: platform.desc,
          })),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'ATS Platforms', item: `${SITE_URL}/ats-platforms` },
        ],
      },
    ];

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-page', 'ats-platforms');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema-page="ats-platforms"]').forEach(el => el.remove());
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredData = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return DATA;
    const out: Record<string, ATSPlatform[]> = {};
    Object.entries(DATA).forEach(([region, items]) => {
      const matches = items.filter(
        item => item.name.toLowerCase().includes(q) || item.desc.toLowerCase().includes(q)
      );
      if (matches.length) out[region] = matches;
    });
    return out;
  }, [search]);

  const activeRegions = useMemo(() => {
    if (activeTab === 'all') return Object.keys(filteredData);
    return filteredData[activeTab] ? [activeTab] : [];
  }, [activeTab, filteredData]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0E1A] text-gray-900 dark:text-[#F8FAFC]">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 dark:bg-gray-800 z-[60]">
        <div
          className="h-full bg-gradient-to-r from-[#6366F1] to-[#818CF8] transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <BlogNavigation />

      {/* ── HERO ── */}
      <div className="relative h-[420px] w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20digital%20illustration%20of%20a%20global%20applicant%20tracking%20system%20network%20with%20glowing%20indigo%20and%20violet%20nodes%20connected%20by%20luminous%20data%20pathways%20spanning%20a%20dark%20world%20map%20silhouette%2C%20futuristic%20HR%20technology%20concept%20with%20floating%20interface%20panels%20and%20recruitment%20workflow%20diagrams%2C%20deep%20navy%20background%20with%20bright%20indigo%20accent%20lights%20radiating%20outward%2C%20minimal%20clean%20tech%20art%20representing%20worldwide%20ATS%20platform%20coverage%2C%20no%20text%2C%20high%20quality%20digital%20illustration&width=1920&height=840&seq=ats-hero-global-v3&orientation=landscape"
          alt="ATS Platforms Coverage Hero"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="max-w-4xl mx-auto px-6 text-center w-full">
            <div className="inline-block px-4 py-1.5 bg-[#6366F1]/20 backdrop-blur-sm border border-[#6366F1]/30 rounded-full mb-6">
              <span className="text-[#818CF8] text-sm font-medium">ATS Autofill Coverage</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-5 leading-tight tracking-tighter">
              Every ATS. One Click.
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto font-medium leading-relaxed">
              JobOS detects the ATS platform automatically and fills every field in one click — no matter where in the world you're applying.
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-gray-300 font-mono">
              <div className="flex items-center gap-2">
                <i className="ri-global-line"></i>
                <span>50+ Countries</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                <span>45s Avg. Apply Time</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line"></i>
                <span>Updated 2026</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS BAR ── (matches Groq dark stats bar) */}
      <div className="w-full bg-[#0d1117] border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-800">
            <div className="py-8 px-8 flex flex-col items-center text-center">
              <span className="text-4xl font-extrabold text-[#818CF8] tracking-tight">{totalCount}+</span>
              <span className="text-xs font-bold text-gray-300 uppercase tracking-widest mt-2">ATS Platforms</span>
              <span className="text-xs text-gray-500 uppercase tracking-widest">Tracked</span>
            </div>
            <div className="py-8 px-8 flex flex-col items-center text-center">
              <span className="text-4xl font-extrabold text-emerald-400 tracking-tight">{liveCount}</span>
              <span className="text-xs font-bold text-gray-300 uppercase tracking-widest mt-2">Autofill Live</span>
              <span className="text-xs text-gray-500 uppercase tracking-widest">One-Click Apply</span>
            </div>
            <div className="py-8 px-8 flex flex-col items-center text-center">
              <span className="text-4xl font-extrabold text-amber-400 tracking-tight">{soonCount}</span>
              <span className="text-xs font-bold text-gray-300 uppercase tracking-widest mt-2">Coming Soon</span>
              <span className="text-xs text-gray-500 uppercase tracking-widest">In Development</span>
            </div>
            <div className="py-8 px-8 flex flex-col items-center text-center">
              <span className="text-4xl font-extrabold text-white tracking-tight">50+</span>
              <span className="text-xs font-bold text-gray-300 uppercase tracking-widest mt-2">Countries</span>
              <span className="text-xs text-gray-500 uppercase tracking-widest">Global Reach</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── META BAR ── (matches blog published/updated bar) */}
      <div className="w-full border-b border-gray-100 dark:border-white/5 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <i className="ri-refresh-line text-[#6366F1]"></i>
            <span className="font-medium text-gray-700 dark:text-gray-300">Last updated:</span>
            <time dateTime="2026-01-01" className="font-mono">January 2026</time>
          </div>
          <div className="w-px h-4 bg-gray-200 dark:bg-white/10 hidden sm:block"></div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
              <i className="ri-shield-check-line text-xs"></i>
              Autofill verified
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] dark:text-[#818CF8] text-xs font-bold">
              <i className="ri-global-line text-xs"></i>
              7 Regions covered
            </span>
          </div>
          <div className="ml-auto text-xs text-gray-400 dark:text-gray-500 font-mono hidden md:block">
            Coverage sourced from jobos.dev internal testing
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* ── SIDEBAR ── */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 space-y-6">

              {/* Legend */}
              <div className="bg-white dark:bg-[#141828]/50 rounded-2xl p-6 border border-gray-200 dark:border-white/10 backdrop-blur-md">
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-5 uppercase tracking-[0.2em] font-mono border-b border-gray-100 dark:border-white/5 pb-4">
                  Status Legend
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="mt-1 w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] flex-shrink-0 animate-pulse"></span>
                    <div>
                      <div className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">Autofill Live</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">One-click form fill active</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="mt-1 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>
                    <div>
                      <div className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">Coming Soon</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">In active development</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="mt-1 w-2 h-2 rounded-full bg-[#818CF8] flex-shrink-0"></span>
                    <div>
                      <div className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">Capture Only</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Data capture supported</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Region nav */}
              <nav className="bg-white dark:bg-[#141828]/50 rounded-2xl p-6 border border-gray-200 dark:border-white/10 backdrop-blur-md">
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-5 uppercase tracking-[0.2em] font-mono border-b border-gray-100 dark:border-white/5 pb-4">
                  Jump to Region
                </h3>
                <ul className="space-y-1.5 text-sm">
                  <li>
                    <button
                      onClick={() => setActiveTab('all')}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all flex items-center justify-between font-bold text-xs cursor-pointer ${
                        activeTab === 'all'
                          ? 'bg-gray-900 dark:bg-white text-white dark:text-black shadow-lg'
                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span>✦</span>
                        <span>All Platforms</span>
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${activeTab === 'all' ? 'bg-white/20 text-white dark:bg-black/20 dark:text-black' : 'bg-gray-100 dark:bg-white/5 text-gray-400'}`}>
                        {totalCount}
                      </span>
                    </button>
                  </li>
                  {Object.entries(REGION_META).map(([key, meta]) => (
                    <li key={key}>
                      <button
                        onClick={() => setActiveTab(key)}
                        className={`w-full text-left px-4 py-3 rounded-xl transition-all flex items-center justify-between font-bold text-xs cursor-pointer ${
                          activeTab === key
                            ? 'bg-gray-900 dark:bg-white text-white dark:text-black shadow-lg'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span>{meta.emoji}</span>
                          <span>{meta.label}</span>
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${activeTab === key ? 'bg-white/20 text-white dark:bg-black/20 dark:text-black' : 'bg-gray-100 dark:bg-white/5 text-gray-400'}`}>
                          {DATA[key].length}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* CTA */}
              <div className="bg-[#6366F1]/5 dark:bg-[#6366F1]/10 rounded-2xl p-6 border border-[#6366F1]/20">
                <h3 className="text-xs font-black text-[#6366F1] dark:text-[#818CF8] mb-3 uppercase tracking-[0.2em] font-mono">
                  Missing your ATS?
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                  We add new platforms every sprint. Let us know which one you need next.
                </p>
                <a
                  href="https://app.jobos.dev/roadmap"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#6366F1] text-white text-[10px] font-black uppercase tracking-widest rounded-full hover:bg-[#4F46E5] transition-all shadow-lg whitespace-nowrap"
                >
                  <i className="ri-road-map-line"></i>
                  View Roadmap
                </a>
              </div>

              {/* Related links */}
              <div className="bg-gray-50 dark:bg-[#141828]/30 rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <h3 className="text-xs font-black text-gray-900 dark:text-white mb-5 uppercase tracking-[0.2em] font-mono border-b border-gray-100 dark:border-white/5 pb-4">
                  Related Resources
                </h3>
                <div className="space-y-3">
                  <Link to="/blog" className="block p-3 rounded-xl border border-gray-900/5 dark:border-white/5 hover:border-[#6366F1]/30 transition-all group">
                    <div className="text-[10px] text-gray-400 mb-1 font-mono">TUTORIALS</div>
                    <div className="text-[11px] font-bold text-gray-900 dark:text-white group-hover:text-[#6366F1]">API Key Setup Guides</div>
                  </Link>
                  <a href="https://app.jobos.dev" target="_blank" rel="noopener noreferrer" className="block p-3 rounded-xl border border-gray-900/5 dark:border-white/5 hover:border-[#6366F1]/30 transition-all group">
                    <div className="text-[10px] text-gray-400 mb-1 font-mono">APP</div>
                    <div className="text-[11px] font-bold text-gray-900 dark:text-white group-hover:text-[#6366F1]">Open JobOS Dashboard</div>
                  </a>
                </div>
              </div>
            </div>
          </aside>

          {/* ── MAIN PANEL ── */}
          <div className="lg:col-span-9">

            {/* Search */}
            <div className="relative mb-10">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-lg"></i>
              <input
                type="text"
                placeholder="Search platforms… e.g. Workday, Greenhouse, Personio"
                className="w-full bg-gray-50 dark:bg-[#141828] border border-gray-200 dark:border-[#2D3148] rounded-2xl py-4 pl-12 pr-4 text-sm text-gray-900 dark:text-[#F8FAFC] placeholder:text-gray-400 dark:placeholder:text-[#475569] focus:outline-none focus:border-[#6366F1] transition-all shadow-sm"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>

            {/* Region sections */}
            <div className="space-y-8">
              {/* Collapse / Expand all — only shown in "all" tab */}
              {activeTab === 'all' && activeRegions.length > 0 && (
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={expandAll}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 dark:border-[#2D3148] text-xs font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-expand-up-down-line"></i>
                    Expand All
                  </button>
                  <button
                    onClick={collapseAll}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 dark:border-[#2D3148] text-xs font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white transition-all cursor-pointer"
                  >
                    <i className="ri-collapse-diagonal-line"></i>
                    Collapse All
                  </button>
                </div>
              )}

              {activeRegions.length > 0 ? (
                activeRegions.map((region, idx) => {
                  const isCollapsed = activeTab === 'all' && collapsedRegions.has(region);
                  const regionPlatforms = filteredData[region] ?? [];
                  const liveInRegion = regionPlatforms.filter(p => p.status === 'live').length;
                  const soonInRegion = regionPlatforms.filter(p => p.status === 'soon').length;

                  return (
                    <div key={region} className="border border-gray-100 dark:border-white/5 rounded-2xl overflow-hidden">
                      {/* Section header — clickable to collapse/expand */}
                      <button
                        onClick={() => activeTab === 'all' && toggleRegion(region)}
                        className={`w-full flex items-center gap-3 px-6 py-5 bg-gray-50/80 dark:bg-[#0d1117]/80 border-b border-gray-100 dark:border-white/5 transition-colors ${activeTab === 'all' ? 'hover:bg-gray-100 dark:hover:bg-white/5 cursor-pointer' : 'cursor-default'}`}
                      >
                        <span className="text-xs font-black text-[#6366F1] tracking-widest font-mono flex-shrink-0">
                          {String(idx + 1).padStart(2, '0')}/{String(activeRegions.length).padStart(2, '0')}
                        </span>
                        <div className="h-px flex-1 bg-gray-200 dark:bg-white/5"></div>
                        <div className="flex items-center gap-2 text-[13px] font-extrabold uppercase tracking-[0.15em] text-gray-500 dark:text-[#475569]">
                          <span className="text-xl">{REGION_META[region].emoji}</span>
                          <span>{REGION_META[region].label}</span>
                          <span className="ml-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-[#94A3B8] text-[10px] font-mono">
                            {regionPlatforms.length}
                          </span>
                        </div>
                        {/* Status mini-badges */}
                        <div className="flex items-center gap-2 ml-2">
                          {liveInRegion > 0 && (
                            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                              {liveInRegion} Live
                            </span>
                          )}
                          {soonInRegion > 0 && (
                            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-[10px] font-bold">
                              {soonInRegion} Soon
                            </span>
                          )}
                        </div>
                        {/* Chevron — only in "all" tab */}
                        {activeTab === 'all' && (
                          <span className={`ml-2 w-6 h-6 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-gray-500 transition-transform duration-300 flex-shrink-0 ${isCollapsed ? '' : 'rotate-180'}`}>
                            <i className="ri-arrow-up-s-line text-sm"></i>
                          </span>
                        )}
                      </button>

                      {/* Platform grid — collapsible */}
                      <div
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${isCollapsed ? 'max-h-0' : 'max-h-[9999px]'}`}
                      >
                        <div className="p-6">
                          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                            {regionPlatforms.map(platform => (
                              <PlatformCard key={platform.name} platform={platform} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-24">
                  <div className="text-5xl mb-6 grayscale opacity-40">🔍</div>
                  <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-3 tracking-tight">No platforms found</h3>
                  <p className="text-gray-500 dark:text-[#94A3B8] mb-8 font-medium">
                    No results for &ldquo;{search}&rdquo; — try a different keyword.
                  </p>
                  <button
                    onClick={() => setSearch('')}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl border border-gray-200 dark:border-[#2D3148] text-sm font-black text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-all cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                    Clear search
                  </button>
                </div>
              )}
            </div>

            {/* ── BOTTOM CTA ── (matches Groq "PUT THIS INTO PRACTICE" block) */}
            <div className="mt-24">
              <div className="bg-[#eff6ff] dark:bg-[#6366F1]/10 rounded-[2.5rem] p-12 md:p-16 border border-[#6366F1]/20 relative overflow-hidden">
                <div className="flex flex-col md:flex-row items-center justify-between gap-12 relative z-10">
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-2 text-[#6366F1] dark:text-[#818CF8] font-black text-xs uppercase tracking-[0.3em] font-mono mb-6">
                      <div className="w-2 h-2 rounded-full bg-current animate-pulse"></div>
                      START APPLYING SMARTER
                    </div>
                    <h3 className="text-4xl font-black text-gray-900 dark:text-white mb-6 tracking-tight">
                      Ready to autofill every ATS?
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-lg font-medium leading-relaxed max-w-xl">
                      Install the JobOS Chrome Extension and apply to any supported ATS in under 45 seconds — no copy-pasting, no re-typing.
                    </p>
                  </div>
                  <a
                    href="https://app.jobos.dev"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-16 px-12 bg-[#6366F1] hover:bg-[#4F46E5] text-white font-black rounded-2xl transition-all shadow-xl shadow-[#6366F1]/20 flex items-center gap-3 group whitespace-nowrap"
                  >
                    Try JobOS Free
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* ── ANIMATED ATS HASHTAG TICKER ── */}
            <div className="mt-20 pt-10 border-t border-gray-100 dark:border-white/5">
              <div className="text-center mb-8">
                <span className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 dark:text-gray-500 font-mono">
                  All Supported Platforms
                </span>
              </div>
              <ATSHashtagTicker />
            </div>

            {/* Back to blog */}
            <div className="mt-10 text-center">
              <Link
                to="/blog"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-sm font-black text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-all shadow-sm"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

/* ── PLATFORM CARD ── */
function PlatformCard({ platform }: { platform: ATSPlatform }) {
  const statusConfig = {
    live: {
      dot: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse',
      badge: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400',
      label: 'Autofill Live',
    },
    soon: {
      dot: 'bg-amber-500',
      badge: 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400',
      label: 'Coming Soon',
    },
    capture: {
      dot: 'bg-[#818CF8]',
      badge: 'bg-[#6366F1]/10 border-[#6366F1]/20 text-[#6366F1] dark:text-[#818CF8]',
      label: 'Capture Only',
    },
  };

  const cfg = statusConfig[platform.status];
  const initials = platform.name.substring(0, 2).toUpperCase();

  return (
    <div className="group bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-white/5 rounded-[1.75rem] overflow-hidden hover:border-[#6366F1]/30 hover:shadow-xl dark:hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.5)] transition-all duration-300 flex flex-col cursor-default">
      <div className="p-7 flex flex-col h-full">
        {/* Top row */}
        <div className="flex items-start justify-between mb-6">
          <div className="w-12 h-12 rounded-2xl bg-gray-50 dark:bg-[#141828] border border-gray-100 dark:border-white/10 flex items-center justify-center text-sm font-black text-[#6366F1] dark:text-[#818CF8] uppercase tracking-tighter shadow-sm group-hover:scale-110 transition-transform duration-500">
            {initials}
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] font-extrabold uppercase tracking-widest ${cfg.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
        cfg.dot === 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse' ? 'bg-emerald-500 animate-pulse' :
        cfg.dot === 'bg-amber-500' ? 'bg-amber-500' : 'bg-[#818CF8]'
      }`}></span>
            {cfg.label}
          </span>
        </div>

        {/* Name */}
        <h3 className="text-lg font-black text-gray-900 dark:text-white mb-3 tracking-tight group-hover:text-[#6366F1] transition-colors leading-snug">
          {platform.name}
        </h3>

        {/* Description */}
        <p className="text-gray-500 dark:text-[#94A3B8] text-[13px] leading-relaxed flex-grow mb-6">
          {platform.desc}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 pt-4 border-t border-gray-100 dark:border-white/5">
          {platform.tags.map(tag => (
            <span
              key={tag}
              className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 text-gray-400 dark:text-[#475569]"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── ANIMATED ATS HASHTAG TICKER ── */
function ATSHashtagTicker() {
  const allPlatforms = Object.values(DATA).flat();

  const row1 = allPlatforms.filter((_, i) => i % 2 === 0).map(p => ({
    name: p.name,
    status: p.status,
  }));
  const row2 = allPlatforms.filter((_, i) => i % 2 !== 0).map(p => ({
    name: p.name,
    status: p.status,
  }));

  const statusColor: Record<string, string> = {
    live:    'text-emerald-500 dark:text-emerald-400 border-emerald-500/20 bg-emerald-500/5',
    soon:    'text-amber-500 dark:text-amber-400 border-amber-500/20 bg-amber-500/5',
    capture: 'text-[#818CF8] border-[#6366F1]/20 bg-[#6366F1]/5',
  };

  const Tag = ({ name, status }: { name: string; status: string }) => (
    <span
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-bold whitespace-nowrap cursor-default select-none transition-all hover:scale-105 ${statusColor[status]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
        status === 'live' ? 'bg-emerald-500 animate-pulse' :
        status === 'soon' ? 'bg-amber-500' : 'bg-[#818CF8]'
      }`}></span>
      #{name}
    </span>
  );

  return (
    <div className="relative overflow-hidden">
      <style>{`
        @keyframes scroll-left {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes scroll-right {
          0%   { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .ticker-left  { animation: scroll-left  50s linear infinite; }
        .ticker-right { animation: scroll-right 50s linear infinite; }
        .ticker-left:hover,
        .ticker-right:hover { animation-play-state: paused; }
      `}</style>

      {/* Fade edges */}
      <div className="pointer-events-none absolute left-0 top-0 h-full w-24 z-10 bg-gradient-to-r from-white dark:from-[#0A0E1A] to-transparent"></div>
      <div className="pointer-events-none absolute right-0 top-0 h-full w-24 z-10 bg-gradient-to-l from-white dark:from-[#0A0E1A] to-transparent"></div>

      {/* Row 1 — scrolls left */}
      <div className="flex overflow-hidden mb-3">
        <div className="ticker-left flex gap-3 w-max">
          {[...row1, ...row1].map((p, i) => (
            <Tag key={`r1-${i}`} name={p.name} status={p.status} />
          ))}
        </div>
      </div>

      {/* Row 2 — scrolls right */}
      <div className="flex overflow-hidden">
        <div className="ticker-right flex gap-3 w-max">
          {[...row2, ...row2].map((p, i) => (
            <Tag key={`r2-${i}`} name={p.name} status={p.status} />
          ))}
        </div>
      </div>
    </div>
  );
}
